// apps/admin/src/lib/uploadFile.ts
//
// dbg-20260622-unified-upload — shared admin-app file-upload client.
//
// The ONE way the admin app uploads files: POST to the unified
// `POST /api/v1/files` endpoint with a `scope`, get back `{ id, url, … }`.
// Replaces the per-feature hand-rolled FormData + fetch + `{file_id}`
// parsing scattered across billing / blueprint / CMS dialogs.
//
// Uses raw fetch with credentials:'include' so the JWT + XSRF cookies both
// travel cross-subdomain (admin-v3 → api-v3). No upload-progress here — the
// admin flows are small docs/images; the merchant-v3 client uses XHR when a
// progress bar is needed.
//
// NOT for artwork (dimension probe / quarantine) — that keeps its own flow.

import { API_BASE_URL, ApiError } from './apiClient.js';

const FILES_UPLOAD_ENDPOINT = '/api/v1/files';

/** Bound translator (namespace 'ops') — optional; low-level callers may
 *  invoke without an i18n scope, in which case the VN literal is used. */
type TFunc = (key: string, vars?: Record<string, unknown>) => string;

/** Scopes accepted by the unified endpoint (mirror BE upload-policies.ts). */
export type UploadScope =
  | 'topup-receipt'
  | 'attachment'
  | 'kyc'
  | 'cms'
  | 'legal'
  | 'product-approval'
  | 'template-area'
  | 'blueprint-mockup'
  | 'misc';

export interface UploadedFile {
  id: string;
  /** Canonical relative path — GET /api/v1/files/:id. */
  url: string;
  scope: UploadScope;
  mime: string;
  bytes: number;
  name: string;
  uploaded_at: string;
}

function readXsrfCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  for (const part of (document.cookie ?? '').split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith('XSRF-TOKEN=')) {
      return decodeURIComponent(trimmed.slice('XSRF-TOKEN='.length));
    }
  }
  return undefined;
}

/**
 * Upload a single file via the unified endpoint. Resolves with the
 * `{ id, url, … }` envelope; throws `ApiError` on a non-2xx / error body.
 */
export async function uploadFile(
  args: {
    file: File;
    scope: UploadScope;
  },
  t?: TFunc,
): Promise<UploadedFile> {
  const base = API_BASE_URL.replace(/\/$/, '');
  const fd = new FormData();
  // `scope` MUST precede the file part so the BE streaming handler reads it
  // before the file body arrives.
  fd.append('scope', args.scope);
  fd.append('file', args.file, args.file.name);

  const headers: Record<string, string> = {};
  const xsrf = readXsrfCookie();
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;

  let res: Response;
  try {
    res = await fetch(`${base}${FILES_UPLOAD_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: fd,
    });
  } catch {
    throw new ApiError(0, {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: t ? t('upload.networkError') : 'Unable to connect to the server.',
      },
    });
  }

  const json = (await res.json().catch(() => null)) as
    | { data?: UploadedFile; error?: { code?: string; message?: string } }
    | null;

  if (!res.ok || !json || !json.data) {
    throw new ApiError(res.status, {
      ok: false,
      error: {
        code: json?.error?.code ?? 'UPLOAD_FAILED',
        message:
          json?.error?.message ??
          (t ? t('upload.failed') : 'File upload failed.'),
      },
    });
  }
  return json.data;
}

/** Convenience: upload then return an absolute URL. The BE already returns a
 *  full absolute URL (dbg-20260622); we only prepend the API base as a
 *  fallback when it came back relative (e.g. PORTAL_URL_API unset). */
export async function uploadFileAbsoluteUrl(
  args: {
    file: File;
    scope: UploadScope;
  },
  t?: TFunc,
): Promise<string> {
  const result = await uploadFile(args, t);
  if (/^https?:\/\//.test(result.url)) return result.url;
  const base = API_BASE_URL.replace(/\/$/, '');
  return `${base}${result.url}`;
}
